import { Fragment } from 'react';
import { Divider, Heading, Paragraph } from '@zillow/constellation';
import type { Answers } from '../../../../../graphql/queries';
import { getAnswersForFields } from '../../../../../utils/overview/overview-display';
import { FIELD_IDS } from '../../../../../constants/wizard';
import { get } from 'lodash-es';
import { formatDate, getFormattedAddress, getPhoneNumberScreenFormat } from '../../../../../utils/formatters';

interface Props {
  answers: Answers;
}

const fieldsInStep = [
  FIELD_IDS.PAST_RESIDENCES_CITY,
  FIELD_IDS.PAST_RESIDENCES_DESCRIPTION,
  FIELD_IDS.PAST_RESIDENCES_MANAGER_EMAIL,
  FIELD_IDS.PAST_RESIDENCES_MANAGER_NAME,
  FIELD_IDS.PAST_RESIDENCES_MANAGER_PHONE,
  FIELD_IDS.PAST_RESIDENCES_START_DATE,
  FIELD_IDS.PAST_RESIDENCES_END_DATE,
  FIELD_IDS.PAST_RESIDENCES_STATE,
  FIELD_IDS.PAST_RESIDENCES_STREET,
  FIELD_IDS.PAST_RESIDENCES_ZIP,
];

export function PastResidencesOverview({ answers }: Props) {
  const answersForFields = getAnswersForFields({ fieldIds: fieldsInStep, answers }) as { [key: string]: Array<string> };

  const { pastResidences } = answers;

  return (
    <>
      {pastResidences &&
        pastResidences.map((pastResidence, index) => {
          const startDate = get(answersForFields[FIELD_IDS.PAST_RESIDENCES_START_DATE], `[${index}]`);
          const endDate = get(answersForFields[FIELD_IDS.PAST_RESIDENCES_END_DATE], `[${index}]`);
          const employerName = get(answersForFields[FIELD_IDS.PAST_RESIDENCES_MANAGER_NAME], `[${index}]`);
          const employerEmail = get(answersForFields[FIELD_IDS.PAST_RESIDENCES_MANAGER_EMAIL], `[${index}]`);
          const employerPhone = get(answersForFields[FIELD_IDS.PAST_RESIDENCES_MANAGER_PHONE], `[${index}]`);
          const description = get(answersForFields[FIELD_IDS.PAST_RESIDENCES_DESCRIPTION], `[${index}]`);

          const formattedAddress = getFormattedAddress({
            city: get(answersForFields[FIELD_IDS.PAST_RESIDENCES_CITY], `[${index}]`),
            state: get(answersForFields[FIELD_IDS.PAST_RESIDENCES_STATE], `[${index}]`),
            street: get(answersForFields[FIELD_IDS.PAST_RESIDENCES_STREET], `[${index}]`),
            zip: get(answersForFields[FIELD_IDS.PAST_RESIDENCES_ZIP], `[${index}]`),
          });

          return (
            <Fragment key={pastResidence?.id ?? index}>
              {index !== 0 && <Divider marginY="md" />}
              <Heading level={6} as="h5" marginY="xs" fontColor="textMedium">
                Past residence #{index + 1}
              </Heading>

              {formattedAddress && (
                <>
                  <Heading level={6} marginY="xs">
                    Address
                  </Heading>
                  <Paragraph>{formattedAddress}</Paragraph>
                </>
              )}
              {startDate && (
                <>
                  <Heading level={6} marginY="xs">
                    Moved in
                  </Heading>
                  <Paragraph>{formatDate(startDate)}</Paragraph>
                </>
              )}
              {endDate && (
                <>
                  <Heading level={6} marginY="xs">
                    Moved out
                  </Heading>
                  <Paragraph>{formatDate(endDate)}</Paragraph>
                </>
              )}
              {(employerName || employerEmail || employerPhone) && (
                <>
                  <Heading level={6} marginY="xs">
                    Landlord contact info
                  </Heading>
                  {employerName && <Paragraph>{employerName}</Paragraph>}
                  {employerEmail && <Paragraph>{employerEmail}</Paragraph>}
                  {employerPhone && <Paragraph>{getPhoneNumberScreenFormat(employerPhone)}</Paragraph>}
                </>
              )}
              {description && (
                <>
                  <Heading level={6} marginY="xs">
                    Description
                  </Heading>
                  <Paragraph>{description}</Paragraph>
                </>
              )}
            </Fragment>
          );
        })}
    </>
  );
}
